import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { RootState, AppDispatch } from '../../../store';
import { 
  setFilters, 
  clearFilters, 
  fetchDependencies 
} from '../../../store/slices/dependenciesSlice';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { 
  DependencyType, 
  DependencyStatus, 
  DependencySeverity, 
  EntityType 
} from '../../../types';

export const DependencyFilters: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters } = useSelector((state: RootState) => state.dependencies);

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[field] = value;
    } else {
      delete newFilters[field];
    }
    dispatch(setFilters(newFilters));
    dispatch(fetchDependencies(newFilters));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchDependencies({ includeResolved: true }));
  };

  const entityTypeOptions = [
    { value: '', label: 'All Types' },
    { value: EntityType.EPIC, label: 'Epic' },
    { value: EntityType.FEATURE, label: 'Feature' },
    { value: EntityType.PROGRAM, label: 'Program' },
    { value: EntityType.INVESTIBLE_STRUCTURE, label: 'Investible Structure' },
  ];

  const dependencyTypeOptions = [
    { value: '', label: 'All Types' },
    { value: DependencyType.BLOCKING, label: 'Blocking' },
    { value: DependencyType.ENABLING, label: 'Enabling' },
    { value: DependencyType.INFORMATIONAL, label: 'Informational' },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: DependencyStatus.OPEN, label: 'Open' },
    { value: DependencyStatus.IN_PROGRESS, label: 'In Progress' },
    { value: DependencyStatus.RESOLVED, label: 'Resolved' },
    { value: DependencyStatus.CANCELLED, label: 'Cancelled' },
  ];

  const severityOptions = [
    { value: '', label: 'All Severities' },
    { value: DependencySeverity.CRITICAL, label: 'Critical' },
    { value: DependencySeverity.HIGH, label: 'High' },
    { value: DependencySeverity.MEDIUM, label: 'Medium' },
    { value: DependencySeverity.LOW, label: 'Low' },
  ];

  const includeResolvedOptions = [
    { value: 'true', label: 'Include Resolved' },
    { value: 'false', label: 'Exclude Resolved' },
  ];

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== undefined && value !== true && value !== ''
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Filters</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-muted-foreground hover:text-foreground flex items-center space-x-1"
          >
            <X className="w-4 h-4" />
            <span>Clear All</span>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <Select
          label="Source Type"
          value={filters.sourceType || ''}
          onChange={(e) => handleFilterChange('sourceType', e.target.value)}
          options={entityTypeOptions}
        />

        <Select
          label="Target Type"
          value={filters.targetType || ''}
          onChange={(e) => handleFilterChange('targetType', e.target.value)}
          options={entityTypeOptions}
        />

        <Select
          label="Dependency Type"
          value={filters.type || ''}
          onChange={(e) => handleFilterChange('type', e.target.value)}
          options={dependencyTypeOptions}
        />

        <Select
          label="Status"
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          options={statusOptions}
        />

        <Select
          label="Severity"
          value={filters.severity || ''}
          onChange={(e) => handleFilterChange('severity', e.target.value)}
          options={severityOptions}
        />

        <Select
          label="Resolution Status"
          value={filters.includeResolved ? 'true' : 'false'}
          onChange={(e) => handleFilterChange('includeResolved', e.target.value === 'true' ? 'true' : 'false')}
          options={includeResolvedOptions}
        />
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          
          {filters.sourceType && (
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-500/10 text-accent-blue text-xs">
              Source: {filters.sourceType.replace('_', ' ')}
              <button
                onClick={() => handleFilterChange('sourceType', '')}
                className="ml-1 hover:text-blue-300"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {filters.targetType && (
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-500/10 text-accent-blue text-xs">
              Target: {filters.targetType.replace('_', ' ')}
              <button
                onClick={() => handleFilterChange('targetType', '')}
                className="ml-1 hover:text-blue-300"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {filters.type && (
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-status-success/10 text-status-success text-xs">
              Type: {filters.type}
              <button
                onClick={() => handleFilterChange('type', '')}
                className="ml-1 hover:text-green-300"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {filters.status && (
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-status-warning/10 text-status-warning text-xs">
              Status: {filters.status.replace('_', ' ')}
              <button
                onClick={() => handleFilterChange('status', '')}
                className="ml-1 hover:text-yellow-300"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {filters.severity && (
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-status-error/10 text-status-error text-xs">
              Severity: {filters.severity}
              <button
                onClick={() => handleFilterChange('severity', '')}
                className="ml-1 hover:text-status-error/80"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          
          {!filters.includeResolved && (
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-500/10 text-muted-foreground text-xs">
              Excluding Resolved
              <button
                onClick={() => handleFilterChange('includeResolved', 'true')}
                className="ml-1 hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};